import React, {useRef, useEffect, useState} from "react";
import FormField from "../../forms/form-field";
import BaseModal from "../../modals/base-modal";
import useLocationStore from "../config/use-location-store";
import FormSelect from "../../forms/form-select";
import Tooltip from "../../tooltip/tooltip";
import {instanceMob} from "../../../utils/utils";
import InlineModal from "../../info-modal/infoModal";
import {getBackendError} from "../../../utils/error-helpers";

const ReceiptModal = ({show, onClose}) => {
    const formRef = useRef(null);
    const [userData, setUserData] = useState(null);
    const [customers, setCustomers] = useState([]);
    const [partners, setPartners] = useState([]);

    const [paymentMethod, setPaymentMethod] = useState("");
    const [isLoading, setIsLoading] = useState(true);
    const [infoModal, setInfoModal] = useState(null);
    const [selectedType, setSelectedType] = useState("customer");
    const [registerNumber, setRegisterNumber] = useState("");
    const [debtValue, setDebtValue] = useState("");

    const selectedLocation = useLocationStore((state) => state.selectedLocation);

    const handlePaymentChange = (e) => {
        const val = e?.target?.value ?? e;
        setPaymentMethod(Number(val));
    };

    const handleOtherPaymentDisplayChange = (e) => {
        const form = formRef.current;
        if (!form) return;
        const hidden = form.elements["other_payment_method"];
        if (hidden) hidden.value = e.target.value;
    };

    const fetchCustomers = () => {
        return instanceMob.get(`admin/customers/accounting`)
            .then((res) => res.data?.map((c) => ({
                value: c?.id,
                label: `${c?.name} (${c.email})`,
            })))
            .catch((err) => {
                console.error("Error cargando clientes:", err);
                return [];
            });
    };

    const fetchPartners = () => {
        return instanceMob.get("admin/customers/elevva-admin")
            .then((res) => res.data.map((p) => ({
                value: String(p.user_id),
                label: `${p.full_name} (${p.email})`,
                email: p.email,
                name: p?.full_name
            })))
            .catch((err) => {
                console.error("Error cargando partners:", err);
                return [];
            });
    };

    const fetchRegisterNumber = (locationId) => {
        if (!locationId) return Promise.resolve("");
        return instanceMob
            .get(`admin/accounting/register-number?location_id=${locationId}`)
            .then((res) => res.data?.next_registry_number || "")
            .catch((err) => {
                console.error("Error cargando número de registro:", err);
                return "";
            });
    };

    const fetchDebt = ({ customerId, partnerId }) => {
        const params = new URLSearchParams();
        if (customerId) params.append("customer_id", customerId);
        if (partnerId) params.append("subject_id", partnerId)
        if (selectedLocation.id) params.append("location_id", selectedLocation.id)

        return instanceMob
            .get(`admin/accounting/people/debt?${params.toString()}`)
            .then((res) => {
                const value = res.data?.debt_value || 0;
                setDebtValue(value);
                const form = formRef.current;
                if (form && form.elements["debt_value"]) {
                    form.elements["debt_value"].value = value;
                }
            })
            .catch((err) => {
                console.error("Error consultando deuda:", err);
                setDebtValue(0);
            });
    };

    const handlePartnerChange = (selected) => {
        const form = formRef.current;
        if (!form) return;

        const selectedPartner = partners.find((p) => p.value === selected?.target.value);

        if (form.elements["partner_id"]) {
            form.elements["partner_id"].value = selected?.target.value || "";
        }
        if (form.elements["subject_email"]) {
            form.elements["subject_email"].value = selectedPartner?.email || "";
        }
        if (form.elements["subject_name"]) {
            form.elements["subject_name"].value = selectedPartner?.name || "";
        }

        if (selected?.target.value) {
            fetchDebt({ customerId: null, partnerId: selected?.target.value });
        }
    };

    const handleCustomerChange = (selected) => {
        const form = formRef.current;
        if (!form) return;

        if (form.elements["customer_id"]) {
            form.elements["customer_id"].value = selected?.target.value || "";
        }

        if (selected?.target?.value) {
            fetchDebt({ customerId: selected?.target.value, partnerId: null });
        }
    };

    const formFields = [
        {
            Component: FormField,
            label: "# Registro",
            name: "registry_number",
            type: "text",
            value: registerNumber,
            placeholder: "Número de registro automático",
            readOnly: true,
            isLoading,
        },
        {
            Component: FormField,
            label: "Usuario que Registra",
            name: "registered_by_user",
            type: "text",
            value: userData
                ? `${userData.first_name} ${userData.last_name} (${userData.email})`
                : "",
            placeholder: "Usuario por defecto",
            readOnly: true,
            isLoading,
        },
        {
            Component: FormField,
            label: "Locación",
            name: "location_name",
            type: "text",
            value: selectedLocation?.name || "",
            placeholder: "Locación por defecto",
            readOnly: true,
            isLoading,
        },
        {
            Component: FormSelect,
            label: "Cliente",
            name: "customer_id",
            options: customers,
            placeholder: "Selecciona un cliente",
            tooltip: "Selecciona al cliente desde la lista desplegable o escribe su nombre para encontrarlo más rápido. Aquí puedes ver todos los clientes registrados en Elevva.",
            searchable: true,
            isLoading,
            required: selectedType === "customer",
            hidden: selectedType !== "customer",
            onChange: handleCustomerChange,
        },
        {
            Component: FormSelect,
            label: "Socio",
            name: "partner_id",
            options: partners,
            placeholder: "Selecciona un socio",
            tooltip: "Selecciona el socio desde la lista desplegable o escribe su nombre para encontrarlo más rápido.",
            searchable: true,
            isLoading,
            required: selectedType === "partner",
            hidden: selectedType !== "partner",
            onChange: handlePartnerChange,
        },
        {
            Component: FormField,
            label: "Valor deuda",
            name: "value_debt",
            type: "currency",
            value: debtValue,
            placeholder: "Valor de la deuda",
            readOnly: true,
            tooltip:
                "Este campo muestra automáticamente el total que el cliente / socio debe actualmente. Se calcula sumando todos los productos adquiridos que aún no han sido pagados por completo.",
            isLoading,
        },
        {
            Component: FormField,
            label: "Valor pago",
            name: "value_payment",
            type: "currency",
            placeholder: "0",
            tooltip: "Ingresa aquí la cantidad que el cliente va a cancelar. Puede corresponder a un abono parcial o al pago total de la deuda según lo acordado.",
            isLoading,
            required: true,
        },
        {
            Component: FormSelect,
            label: "Método de pago",
            name: "payment_method",
            options: [
                {value: 10, label: "Tarjeta de Crédito o Débito"},
                {value: 20, label: "Transferencia"},
                {value: 30, label: "Efectivo"},
                {value: 40, label: "No registra"},
                {value: 50, label: "Otros"},
            ],
            placeholder: "Selecciona una opción",
            tooltip: "Selecciona cómo se realizará el pago: tarjeta, efectivo o transferencia bancaria.",
            onChange: handlePaymentChange,
            isLoading,
            required: true,
        },
        {
            Component: FormField,
            label: "Entidad",
            name: "other_payment_method_display",
            placeholder: "Escribe el nombre de la entidad",
            tooltip: "Especifica por qué plataforma se hizo la transferencia: Nequi, Daviplata, Efecty u otra entidad.",
            disabled: paymentMethod !== 50,
            onChange: handleOtherPaymentDisplayChange,
            isLoading,
            required: paymentMethod === 50,
        },
        {
            Component: FormField,
            label: "Concepto de venta",
            name: "sale_concept",
            type: "text",
            placeholder: "Escribe el concepto",
            isLoading,
            required: true,
        },
    ];

    const handleSubmit = (e) => {
        e.preventDefault();
        const formData = new FormData(formRef.current);
        const payload = Object.fromEntries(formData.entries());

        if (selectedType === "customer") {
            delete payload.partner_id;
        } else if (selectedType === "partner") {
            delete payload.customer_id;
        }

        instanceMob
            .post("admin/accounting/receipt/create", payload)
            .then(() => {
                setInfoModal({
                    iconType: "success",
                    title: "Recibo creado",
                    description: "El recibo fue registrado con éxito.",
                    primaryAction: {
                        label: "Aceptar",
                        onClick: () => {
                            setInfoModal(null);
                            onClose();
                        },
                    },
                });
            })
            .catch((err) => {
                const errorMessage = getBackendError(err);
                setInfoModal({
                    iconType: "error",
                    title: "Error",
                    description: errorMessage,
                    secondaryAction: {
                        label: "Cerrar",
                        onClick: () => setInfoModal(null),
                    },
                });
            });
    };

    useEffect(() => {
        const storedUser = {
            email: localStorage.getItem("email"),
            first_name: localStorage.getItem("first_name"),
            last_name: localStorage.getItem("last_name"),
            id: localStorage.getItem("id"),
        };
        setUserData(storedUser);
    }, []);

    useEffect(() => {
        if (!show) return;
        setIsLoading(true);

        Promise.all([fetchCustomers(), fetchPartners(), fetchRegisterNumber(selectedLocation?.id)])
            .then(([customersData, partnersData, registerNum]) => {
                setCustomers(customersData);
                setPartners(partnersData);
                setRegisterNumber(registerNum);
            })
            .finally(() => setIsLoading(false));
    }, [show, selectedLocation?.id]);

    useEffect(() => {
        const form = formRef.current;
        if (!form) return;
        if (paymentMethod !== 50) {
            if (form.elements["other_payment_method_display"]) form.elements["other_payment_method_display"].value = "";
            if (form.elements["other_payment_method"]) form.elements["other_payment_method"].value = "";
        }
    }, [paymentMethod]);

    return (
        <>
            <BaseModal
                show={show}
                onClose={onClose}
                title="Recibo de Caja"
                onSave={() => formRef.current.requestSubmit()}
                size={"lg"}
            >
                <form ref={formRef} onSubmit={handleSubmit} className="d-flex flex-column gap-2">
                    <div className="d-flex gap-2 align-items-end">
                        <div className="w-50">
                            <FormField
                                label="Fecha"
                                name="receipt_date"
                                type="date"
                                value={new Date().toISOString().split("T")[0]}
                                readOnly={true}
                                placeholder="Fecha del recibo"
                                width="100%"
                                isLoading={isLoading}
                            />
                        </div>
                        <div className="d-flex gap-2">
                            <button
                                type="button"
                                className={`btn ${
                                    selectedType === "customer"
                                        ? "base-modal-done-button"
                                        : "base-modal-cancel-button"
                                }`}
                                onClick={() => setSelectedType("customer")}
                            >
                                Cliente
                            </button>

                            <button
                                type="button"
                                className={`btn ${
                                    selectedType === "partner"
                                        ? "base-modal-done-button"
                                        : "base-modal-cancel-button"
                                }`}
                                onClick={() => setSelectedType("partner")}
                            >
                                Socio
                            </button>
                        </div>
                    </div>
                    {formFields.map((field, index) => {
                        if (field.hidden) return null;
                        const FieldComponent =
                            typeof field.Component === "string"
                                ? React.createElement(field.Component, {...field})
                                : <field.Component {...field} />;

                        if (field.tooltip) {
                            return (
                                <Tooltip key={index} title={field.label} description={field.tooltip} position="right"
                                         variant="rich">
                                    {FieldComponent}
                                </Tooltip>
                            );
                        }
                        return <React.Fragment key={index}>{FieldComponent}</React.Fragment>;
                    })}
                    <input type="hidden" name="other_payment_method"/>
                    <input type="hidden" name="location_id" value={selectedLocation?.id || ""}/>
                    <input type="hidden" name="subject_email"/>
                    <input type="hidden" name="subject_name"/>
                </form>
            </BaseModal>
            {infoModal && (
                <InlineModal
                    icon={infoModal.icon}
                    iconType={infoModal.iconType}
                    title={infoModal.title}
                    description={infoModal.description}
                    primaryAction={infoModal.primaryAction}
                    secondaryAction={infoModal.secondaryAction}
                />
            )}
        </>
    );
};

export default ReceiptModal;
